import { FirebaseAdmin } from 'fitbeat.firebase';
import {
  AgreementType,
  BadRequestError,
  BaseEquipment,
  BaseUserProfile,
  bodyComposition,
  Equipment,
  EquipmentCombinations,
  Gym,
  GymEnvironment,
  IAccountChangeStatus,
  IAccountChangeValidation,
  IAndroidHealthConnectWorkoutImportConfig,
  IAppleHealthWorkoutImportConfig,
  IBodyAssessment,
  IEmailsSent,
  IEnabledFeatures,
  IGarminWorkoutImportConfig,
  IHeartRateMonitors,
  IPaymentCancelledDetails,
  IPlanChangedDetails,
  IStripeCustomerDetailsWrapper,
  IStripeCustomerSubscriptionDataWrapper,
  IStripeSubscriptionData,
  ISubscriptionCreateArguments,
  KnownMediums,
  LeadStatus,
  LegalAgreement,
  NotFoundError,
  PlatformType,
  profileColors,
  ProgramType,
  StrengthRatioForMuscleGroups,
  TimeSlotType,
  WorkoutImportOrigin,
} from 'fitbeat.models';
import { NotificationService } from 'fitbeat.notification-service';
import { MemberContractDetails, PaymentService } from 'fitbeat.payment-service';
import Stripe from 'stripe';
import { FamilyPlanConfig } from '../../config/familyplanconfig';
import { Level, UserProfile } from '../../entities';
import {
  createContinueSignupLink,
  initializeStripeDetails,
  initializeUserFromSubsetOfFields,
  updateBasicUserInfoAfterInitialAuthSetup,
  updateLevelAndGoalOnUser,
  updateMemberDetailsOnUser,
  updateWeightHeightAndStudioHRMonitorOnUser,
  validatePreAccountDetailsForAppSignup,
  validatePreAccountDetailsForLoginTerminal,
} from '../../entities/initializers';
import { DbGymRepository, DbUserProfileRepository, IUserProfileRepository } from '../../persistence';
import { AccountStatusManager } from '../accountstatus';
import { DynamicLinkService } from '../dynamic-link';
import { FamilyPlanService } from '../familyplan';
import { MarketingAnalyticsService } from '../marketing-analytics';
import { SpecialOfferManager } from '../member';
import { EnabledFeaturesHydrationService } from './EnabledFeaturesHydrationService';

export class UserProfileService {
  public static mapStripeSubscriptionToUserProfileFields = (
    planId: string,
    timeSlotTypeString: string,
    planExpiresAtFromStripe: number,
    currentPlanCancelAtEndOfTerm: boolean,
    currentPlanStatus: string,
    stripeSubscriptionData?: IStripeSubscriptionData,
  ) => {
    const timeSlotType: TimeSlotType = (TimeSlotType as any)[timeSlotTypeString];
    const updatedProfileSubscriptionChanges = {
      currentPlanId: planId,
      currentPlanExpiresAt: (planExpiresAtFromStripe * 1000),
      timeSlotType,
      currentPlanStatus,
      currentPlanCancelAtEndOfTerm,
      stripeSubscriptionData,
    };
    return updatedProfileSubscriptionChanges;
  };

  private paymentService: PaymentService;

  private userRepo: IUserProfileRepository;

  constructor(
    paymentService: PaymentService,
    private notificationService: NotificationService,
    private userSettingsDb: any, // todo: use userRepo instead of userSettingsDb, then replace params
    private logger: any,
    private firebaseAdmin: FirebaseAdmin,
    private marketingAnalyticsService: MarketingAnalyticsService,
    private dynamicLinkService: DynamicLinkService,
  ) {
    this.paymentService = paymentService;
    this.userRepo = new DbUserProfileRepository(this.userSettingsDb);
  }

  public getUserProfile = async (uid: string): Promise<UserProfile | undefined> => {
    try {
      this.logger.debug(`loading user profile for uid: ${uid}`);
      return await this.userRepo.get(uid);
    } catch (e) {
      if (this.errorIsNotFound(e)) {
        return;
      }
      throw e;
    }
  };

  public updateOnlyUserChangeableFields = async (uid: string, newUserProfile: UserProfile) => {
    try {
      const currentUserProfile = await this.getUserProfile(newUserProfile.uid);
      if (!currentUserProfile) {
        throw new Error('user not found');
      }
      if (newUserProfile.dateOfBirth === '') {
        newUserProfile.dateOfBirth = currentUserProfile.dateOfBirth;
      }

      if (currentUserProfile
        && ((currentUserProfile.longTermGoal !== newUserProfile.longTermGoal)
          || currentUserProfile.skillLevel !== newUserProfile.skillLevel)
      ) {
        const todaysDate = new Date().toISOString();
        newUserProfile.weeklyWorkoutMinutesTarget = [
          {
            dateOfTargetChange: todaysDate,
            targetAboveBaseline: 0,
          },
        ];
      }

      const userProfilePatch: any = UserProfile.mapUserUpdateableFields(newUserProfile);

      /*
      Only legacy apps will set currentUserProfile.heartRateMonitorIdentifier in this function.
      Setting heartRateMonitors.home and heartRateMonitors.studio is to enable forward compatability
      for apps released before 15 Aug 2024
      */
      const updatingHRMIdInLegacyApp = currentUserProfile.heartRateMonitorIdentifier
        !== userProfilePatch.heartRateMonitorIdentifier;
      if (updatingHRMIdInLegacyApp) {
        this.logger.info(`Updating user hrm id from legacy app for user: ${uid}`);
        userProfilePatch.heartRateMonitors = {
          home: newUserProfile.heartRateMonitorIdentifier,
          studio: newUserProfile.heartRateMonitorIdentifier,
        };
      }
      const result = await this.updateSpecificFieldsWithConflictRetry(uid, userProfilePatch);
      return new UserProfile(uid, result);
    } catch (e) {
      if (this.errorIsNotFound(e)) {
        return null;
      }
      throw e;
    }
  };

  public updateUserProfile = async (uid: string, updatedUserProfile: any) => {
    this.logger.info(`updating user profile for uid: ${uid}`);
    const userProfile: any = await this.userSettingsDb.get(uid);
    if (!userProfile) {
      throw new Error('user not found');
    }
    updatedUserProfile._id = userProfile._id;
    updatedUserProfile._rev = userProfile._rev;
    if (!updatedUserProfile.emailsSent) {
      const defaultEmailsSent: IEmailsSent = {
        firstTimeSignin: false,
        trailAboutToExpire: false,
        onboarding: false,
        checkIn: false,
        accountCreated: false,
        firstWorkoutBooked: false,
        postFirstWorkout: false,
        postSecondWorkout: false,
      };
      updatedUserProfile.emailsSent = defaultEmailsSent;
    }
    await this.userRepo.update(updatedUserProfile);
    if (updatedUserProfile.stripeUserId
      && UserProfile.hasRequiredStripeBillingDetails(updatedUserProfile)
      && this.hasStripeBillingChanged(userProfile, updatedUserProfile)) {
      await this.paymentService.updateStripeCustomerShippingDetails(updatedUserProfile);
    }
    return updatedUserProfile;
  };

  public updateUserWeight = async (uid: string, weight: number) => {
    const userProfile: any = await this.userSettingsDb.get(uid);
    if (!userProfile) {
      throw new Error('user not found');
    }
    const updatedUserProfile = {
      ...userProfile,
      weight,
    };
    await this.userRepo.update(updatedUserProfile);
  };

  public addUserSignupWorkoutInvite = async (uid: string, workoutInviteId: string) => {
    const userProfile: any = await this.userSettingsDb.get(uid);
    if (!userProfile) {
      throw new Error('user not found');
    }
    userProfile.signupSource = !userProfile.signupSource
      ? { signupWorkoutInviteId: workoutInviteId }
      : { ...userProfile.signupSource, signupWorkoutInviteId: workoutInviteId };
    await this.userRepo.update(userProfile);
  };

  public removeUserSignupWorkoutInvite = async (uid: string) => {
    const userProfile: any = await this.userSettingsDb.get(uid);
    if (!userProfile) {
      throw new Error('user not found');
    }

    if (userProfile.signupSource?.signupWorkoutInviteId) {
      delete userProfile.signupSource?.signupWorkoutInviteId;
      await this.userRepo.update(userProfile);
    }
  };

  public completeStrengthAssessment = async (
    uid: string,
    strengthRatioForMuscleGroups: StrengthRatioForMuscleGroups,
  ) => {
    const userProfile: any = await this.userRepo.get(uid);
    if (!userProfile) {
      throw NotFoundError.User(uid);
    }
    const updatedUserProfile = {
      ...userProfile,
      strengthRatioForMuscleGroups,
    };
    await this.userRepo.update(updatedUserProfile);
  };

  public getUserIdByEmail = async (
    email: string,
  ): Promise<UserProfile | undefined> => await this.userRepo.getUserByEmail(email) || undefined;

  public getUserByWorkoutImportConfig = async (
    origin: WorkoutImportOrigin,
    userId: string,
  ): Promise<UserProfile> => await this.userRepo.getUserByWorkoutImportConfig(origin, userId);

  public updateUserSubscriptionDetails = async (
    uid: string,
    subscriptionId?: string,
    planId?: string,
    shouldAddPlanHistory = true,
  ) => {
    try {
      const userProfile = await this.getUserProfile(uid);
      if (!userProfile) {
        throw NotFoundError.User(uid);
      }

      const subscription = await this.paymentService.getSubscriptionWithSchedule(
        planId,
        subscriptionId,
      );

      if (!subscription || subscription.status === 'canceled') {
        const removeProfileSubscriptionChanges = {
          timeSlotType: TimeSlotType.None,
          currentPlanStatus: 'canceled',
        };
        if (this.hasSubscriptionRemoveDetailsChanged(userProfile, removeProfileSubscriptionChanges)) {
          this.logger.info('updating subscription remove details');
          userProfile.clearCurrentPlanAndTimeSlotType(userProfile.currentPlanId);
          await this.updateSpecificFieldsWithConflictRetry(userProfile.uid, userProfile);
        }
        return;
      }

      const timeSlotString = this.paymentService.getTimeSlotStringFromMetadata(subscription);
      const {
        plan,
        current_period_end,
        status,
        cancel_at_period_end,
      } = subscription;
      const customer = subscription.customer as Stripe.Customer;
      const latestInvoice = subscription.latest_invoice as Stripe.Invoice;
      if (!plan) {
        throw new Error('updateUserSubscriptionDetails requires a stripe plan');
      }
      const contractDetails = MemberContractDetails.calculateContractDetails(
        this.logger,
        subscription as IStripeCustomerSubscriptionDataWrapper,
      );
      const subscriptionStartsAt = subscription.start_date ? (subscription.start_date * 1000) : undefined;
      const subscriptionCancelsAt = subscription.cancel_at ? (subscription.cancel_at * 1000) : undefined;
      const updatedProfileSubscriptionChanges = UserProfileService.mapStripeSubscriptionToUserProfileFields(
        plan.id,
        timeSlotString,
        current_period_end,
        cancel_at_period_end,
        status,
        {
          accountBalance: customer.balance,
          contractEndDate: contractDetails.contractEnd,
          contractStartDate: contractDetails.contractStart,
          extendedContractInDays: contractDetails.extendedContractInDays,
          isContract: true,
          hasShedule: !!(subscription.schedule),
          subscriptionStartsAt,
          subscriptionCancelsAt,
          delinquent: customer.delinquent,
          paymentId: latestInvoice.charge && latestInvoice.charge as string,
          paymentAmount: latestInvoice.amount_due,
          planDescription: subscription.plan && subscription.plan.nickname,
          discountApplied: subscription.discount && subscription.discount.coupon.amount_off,
          discountCodeApplied: subscription.discount && subscription.discount.coupon.id,
          discountEnd: subscription.discount && subscription.discount.end,
          paymentStatus: subscription.status,
        },
      );

      if (this.hasSubscriptionUpdateDetailsChanged(userProfile, updatedProfileSubscriptionChanges)) {
        this.logger.info('updating subscription update details');
        const { isFamilyPlan } = await this.getUserProfileAttributesFromProductMetadata(plan);

        if (isFamilyPlan) {
          FamilyPlanService.assignAvailableProfileColor(userProfile, []);
        }

        userProfile.setSubscriptionDetails({
          ...updatedProfileSubscriptionChanges,
          isFamilyPlanAdmin: !!isFamilyPlan,
        });

        if (shouldAddPlanHistory) {
          if (!userProfile.planHistory) {
            userProfile.planHistory = [plan.id];
          } else {
            userProfile.planHistory.push(plan.id);
          }
        }
        await this.userRepo.update(userProfile);
        this.marketingAnalyticsService.syncCustomerToProvidersInBackground(userProfile);
      }
    } catch (e) {
      if (e.status === 409) {
        this.logger.error(`conflict while updating subscription for ${uid}, retrying`);
        await this.updateUserSubscriptionDetails(
          uid,
          subscriptionId,
          planId,
          shouldAddPlanHistory,
        );
      } else {
        throw e;
      }
    }
  };

  public setSubscriptionCancelAtEndOfTerm = async (uid: string) => {
    const userProfile = await this.getUserProfile(uid);
    if (!userProfile) {
      throw new Error(`user not found for ${uid}`);
    }
    userProfile.setSubscriptionDetails({
      currentPlanId: userProfile.currentPlanId,
      currentPlanExpiresAt: userProfile.currentPlanExpiresAt,
      timeSlotType: userProfile.timeSlotType,
      currentPlanStatus: userProfile.currentPlanStatus,
      currentPlanCancelAtEndOfTerm: true,
      isFamilyPlanAdmin: userProfile.isFamilyPlanAdmin,
    });
    await this.userRepo.update(userProfile);
  };

  public sendPaymentSubscriptionCancellationNotification = async (paymentDetails: IPaymentCancelledDetails) => {
    await this.notificationService.paymentSubscriptionCancelled(paymentDetails);
  };

  public sendPlanChangedNotification = async (paymentDetails: IPlanChangedDetails) => {
    await this.notificationService.planChanged(paymentDetails);
  };

  public addDeviceTokenToUser = async (uid: string, token: string, platform: PlatformType) => {
    const userProfile = await this.getUserProfile(uid);
    if (!userProfile) {
      throw new Error(`user not found for ${uid}`);
    }

    if (this.notificationService.addDeviceToken(userProfile, token, platform)) {
      await this.userRepo.update(userProfile);
    }
  };

  public getUserProfileByStripeId = async (stripeUserId: string): Promise<UserProfile | undefined> => {
    this.logger.info(`getting user profile using stripe customer ${stripeUserId}`);
    const result = await this.userSettingsDb.find({
      selector: {
        stripeUserId,
      },
      use_index: [
        '_design/userProfileStripeUserIdIndex',
        'userProfileStripeUserIdIndex',
      ],
    });
    if (!result || result.docs.length > 1) {
      throw new Error(`more than one user found with customerId: ${stripeUserId}`);
    }
    return result.docs[0];
  };

  public personalTrainerApproveUser = async (uid: string, approveDate: Date): Promise<UserProfile> => {
    const userProfile = await this.getUserProfile(uid);
    if (!userProfile) {
      throw new Error(`user not found for ${uid}`);
    }
    if (userProfile.ptApprovedAt == null) { // only requires approval once
      userProfile.ptApprovedAt = approveDate.getTime();
      // TODO add approver name
      await this.userRepo.update(userProfile);
    }
    return userProfile;
  };

  public getUserProfileByEnrolledFaceId = async (
    enrolledFaceId: string,
  ) => this.userRepo.getUserByEnrolledFaceId(enrolledFaceId);

  public updateEnrolledFaceId = async (uid: string, enrolledFaceId: string) => {
    const update = {
      enrolledFaceId,
    };
    return this.updateSpecificFieldsWithConflictRetry(uid, update);
  };

  public updateLeadStatus = async (uid: string, leadStatus: LeadStatus) => {
    const update = {
      leadStatus,
    };
    return this.updateSpecificFieldsWithConflictRetry(uid, update);
  };

  public removeEnrolledFaceId = async (uid: string) => {
    const update = {
      enrolledFaceId: undefined,
    };
    return this.updateSpecificFieldsWithConflictRetry(uid, update);
  };

  public setExtendedFreeTrial = async (uid: string) => {
    const update = {
      hasUsedExtendedFreeTrail: true,
    };
    return this.updateSpecificFieldsWithConflictRetry(uid, update);
  };

  public signupFromLoginTerminal = async (userParams: Partial<UserProfile>) => {
    userParams.signupSource = {
      medium: KnownMediums.walkin,
      source: userParams.gymName || '',
    };
    const userProfile = await this.signupPreAccount(userParams, validatePreAccountDetailsForLoginTerminal);
    return userProfile;
  };

  public preAccountSignup = async (userParams: Partial<UserProfile>) =>
    // TODO: add field validations
    this.signupPreAccount(userParams, () => null);

  public preAccountSignupFromApp = async (
    userParams: Partial<UserProfile>,
  ) => this.signupPreAccount(userParams, validatePreAccountDetailsForAppSignup);

  public preAccountSignupFromAppAndSubscribeToTrial = async (
    userParams: Partial<UserProfile>,
    accountStatusManager: AccountStatusManager,
  ) => {
    const userProfile = await this.signupPreAccount(userParams, validatePreAccountDetailsForAppSignup);
    if (userProfile.gymName) {
      const gym = DbGymRepository.getByName(userProfile.gymName);
      await this.subscribeNewUserToApplicableFreePlanIfAllowed(userProfile.uid, gym, accountStatusManager);
    }
    return this.getUserProfile(userProfile.uid);
  };

  public async canContinueAuthFlow(uid: string, authType: string, isNewUser: boolean) {
    this.logger.debug(`canContinueAuthFlow for uid: ${uid}, authType: ${authType}, newUser: ${isNewUser}`);
    if (isNewUser && authType === 'login') {
      await this.firebaseAdmin.deleteUser(uid);
      throw new Error('You do not have an account yet. Get Started to sign up for a new account.');
    }

    const firebaseUser = await this.firebaseAdmin.getUserByUid(uid);
    if (!firebaseUser.email) {
      // handle as user not found. could load using uid, but then why not always load with uid?
      return;
    }
    const userByEmail = await this.getUserIdByEmail(firebaseUser.email);
    if (!userByEmail) {
      return;
    }

    const createdNewAccountWithoutFollowingSignupLink = userByEmail.continueSignupToken
      && firebaseUser.uid !== userByEmail.uid;
    if (createdNewAccountWithoutFollowingSignupLink) {
      await this.firebaseAdmin.deleteUser(uid);
      let errorMessage = 'Please follow the link your received in your inbox to complete your signup and try again. ';
      errorMessage += 'This link is required to load the information and workout you captured at the gym.';
      throw new Error(errorMessage);
    }

    if (!userByEmail.continueSignupToken && firebaseUser.uid !== userByEmail.uid) {
      await this.firebaseAdmin.deleteUser(uid);
      throw new Error('A user already exists with this email.');
    }

    const preAccountWithValidFirebaseUser = firebaseUser.uid === userByEmail.uid && userByEmail.continueSignupToken;
    if (preAccountWithValidFirebaseUser) {
      await this.userRepo.removeContinueSignupToken(userByEmail.uid);
    }
  }

  public updateMemberDetails = async (uid: string, updatedMember: any) => {
    const userProfile = await this.getUserProfile(uid);
    if (!userProfile) {
      throw new Error(`user not found for ${uid}`);
    }
    const userProfileUpdate = await updateMemberDetailsOnUser(
      userProfile,
      this.userRepo,
      updatedMember.mobileNumber,
      updatedMember.email,
      updatedMember.skillLevel,
      updatedMember.longTermGoal,
      updatedMember.heartRateMonitorIdentifier,
      updatedMember.dateOfBirth,
      updatedMember.gender,
      updatedMember.addressLine1,
      updatedMember.addressLine2,
      updatedMember.postCode,
      updatedMember.suburb,
      updatedMember.unit,
      updatedMember.state,
      updatedMember.weight,
      updatedMember.height,
      updatedMember.nickname,
      updatedMember.firstname,
      updatedMember.lastname,
      updatedMember.bio,
      updatedMember.isStreamingUser,
      updatedMember.discount,
      updatedMember.source,
      updatedMember.attributionSourceId,
      updatedMember.signupSource,
    );

    await this.userRepo.update(userProfileUpdate);
    return userProfileUpdate;
  };

  public updateLevelAndGoal = async (uid: string, skillLevel: Level, goal: ProgramType) => {
    const userProfile = await this.getUserProfile(uid);
    if (!userProfile) {
      throw new Error(`user not found for ${uid}`);
    }
    updateLevelAndGoalOnUser(userProfile, skillLevel, goal);

    await this.userRepo.update(userProfile);
  };

  public updateWeightHeightAndHRMonitor = async (
    uid: string,
    weight: string,
    height: string,
    hrMonitor: string,
  ) => {
    const userProfile = await this.getUserProfile(uid);
    if (!userProfile) {
      throw new Error(`user not found for uid ${uid}`);
    }
    updateWeightHeightAndStudioHRMonitorOnUser(userProfile, weight, height, hrMonitor);
    await this.userRepo.update(userProfile);
  };

  public updateBasicUserInfoAfterInitialAuthSetup = async (uid: string, userParams: any) => {
    const userProfile = await this.getUserProfile(uid);
    if (!userProfile) {
      throw new Error(`user not found for ${uid}`);
    }

    updateBasicUserInfoAfterInitialAuthSetup(userProfile, userParams);
    await this.userRepo.update(userProfile);
  };

  public addBodyAssessment = async (uid: string, assessment: IBodyAssessment, composition: bodyComposition) => {
    const userProfile = await this.getUserProfile(uid);
    if (!userProfile) {
      throw new Error(`user not found for ${uid}`);
    }

    userProfile.bodyAssessment = assessment;
    userProfile.bodyComposition = composition;
    return this.userRepo.update(userProfile);
  };

  public removeBodyScale = async (uid: string) => {
    const userProfile = await this.getUserProfile(uid);
    if (!userProfile) {
      throw new Error(`user not found for ${uid}`);
    }

    delete userProfile.bodyAssessment;
    delete userProfile.bodyComposition;
    return this.userRepo.update(userProfile);
  };

  public updateBodyComposition = async (uid: string, composition: bodyComposition) => {
    const userProfile = await this.getUserProfile(uid);
    if (!userProfile) {
      throw new Error(`user not found for ${uid}`);
    }

    userProfile.bodyComposition = composition;
    return this.userRepo.update(userProfile);
  };

  public updateUserProfileColor = async (uid: string, color: string) => {
    const userProfile = await this.getUserProfile(uid);
    if (!userProfile) {
      throw new Error(`user not found for ${uid}`);
    }
    if (!profileColors.includes(color)) {
      throw new Error('Invalid profile color');
    }
    userProfile.familyPlanSettings = {
      ...userProfile.familyPlanSettings,
      profileColor: color,
    };
    return this.userRepo.update(userProfile);
  };

  public updateUserProfilePin = async (uid: string, pin: string) => {
    const userProfile = await this.getUserProfile(uid);
    if (!userProfile) {
      throw new Error(`user not found for ${uid}`);
    }
    if (!(pin.replace(/[^0-9]/g, '').length === FamilyPlanConfig.pinLength)) {
      throw new Error('Invalid pin input');
    }
    userProfile.familyPlanSettings = {
      ...userProfile.familyPlanSettings,
      pin,
      hasPin: true,
    };
    return this.userRepo.update(userProfile);
  };

  public completePreAccountUserSignup = async (
    userProfile: UserProfile,
    legalAgreements: AgreementType[],
  ) => {
    if (legalAgreements && LegalAgreement.validAgreementTypeList(legalAgreements)) {
      await this.addLegalAgreementsToUser(userProfile, legalAgreements);
    } else {
      throw new BadRequestError('Legal agreement not accepted');
    }
    return this.userRepo.removeContinueSignupToken(userProfile.uid);
  };

  public addLegalAgreementsToUser = async (userProfile: UserProfile, legalAgreements: AgreementType[]) => {
    const newAgreements = LegalAgreement.createSignedInstances(legalAgreements);
    userProfile.legalAgreements = LegalAgreement.mergeLegalAgreements(userProfile.legalAgreements, newAgreements);
    return this.updateSpecificFieldsWithConflictRetry(userProfile.uid, userProfile);
  };

  public async addUserToFamilyPlan(uid: string, familyPlanOwnerUserProfileId: string) {
    const userProfile = await this.getUserProfile(uid);

    if (!userProfile) {
      throw new Error(`user not found for ${uid}`);
    }

    userProfile.planOwnerUserProfileId = familyPlanOwnerUserProfileId;

    return this.userRepo.update(userProfile);
  }

  public setStripeIdIfExistingCustomerDoesNotHavePaymentSource = async (
    userProfile: UserProfile,
    planId: string,
    stripeCustomerIdCreatedBy3rdParty?: string,
  ): Promise<void> => {
    if (!stripeCustomerIdCreatedBy3rdParty) {
      return;
    }
    const stripeIdWithValidPaymentSource = await this.paymentService.getStripeCustomerIdWithValidPaymentSource(
      userProfile.stripeUserId,
      stripeCustomerIdCreatedBy3rdParty,
      planId,
    );

    const shouldUpdateUserProfileWithStripeIdCreatedBy3rdParty = stripeIdWithValidPaymentSource
      && stripeIdWithValidPaymentSource !== userProfile.stripeUserId;
    if (shouldUpdateUserProfileWithStripeIdCreatedBy3rdParty) {
      userProfile.stripeUserId = stripeIdWithValidPaymentSource as string;
      await this.updateUserProfile(userProfile.uid, userProfile);
    }
  };

  public getEnabledFeaturesForUser = async (uid: string, gymEnvironment: GymEnvironment):
  Promise<IEnabledFeatures> => {
    const currentUserProfile = await this.getUserProfile(uid);
    if (!currentUserProfile) {
      throw new Error(`user not found: ${uid}`);
    }
    const enabledFeaturesService = new EnabledFeaturesHydrationService(this.paymentService, this, gymEnvironment);
    return enabledFeaturesService.build(currentUserProfile);
  };

  public getEnabledFeaturesFromUserProfile = async (
    userProfile: UserProfile,
    gymEnvironment: GymEnvironment,
  ): Promise<IEnabledFeatures> => {
    const enabledFeaturesService = new EnabledFeaturesHydrationService(this.paymentService, this, gymEnvironment);
    return enabledFeaturesService.build(userProfile);
  };

  public validateUserStripeAccountForPlanChange = async (
    userId: string,
    planId: string,
    accountStatusManager: AccountStatusManager,
    gymEnvironment: GymEnvironment,
    gymName?: string,
  ): Promise<IAccountChangeValidation> => {
    const user = await this.userRepo.getUserProfileOrThrow(userId);

    const customerExistsOnAccountWithPlanId = await this.paymentService.customerExistsOnAccount(
      user.stripeUserId,
      planId,
    );
    if (customerExistsOnAccountWithPlanId) {
      return {
        accountChangeNeeded: false,
      };
    }

    const newAccountId = this.paymentService.getAccountIdForPlanId(planId);
    const changingToGym = gymName ? DbGymRepository.getByName(gymName)
      : DbGymRepository.getByAccountId(newAccountId, gymEnvironment);
    // Todo: Change this logic to support home
    if (!changingToGym || !changingToGym.dummyPriceIdForSignup) {
      throw new Error('Invalid accountId');
    }

    const paymentInfo = await this.paymentService.getPaymentInfo(user, user.currentPlanId);
    const userAccountStatus = await accountStatusManager.getAccountStatus(
      user,
      paymentInfo.currentPlan,
      gymEnvironment,
    );

    return {
      accountChangeNeeded: true,
      currentAccountStatus: userAccountStatus,
      changeTo: {
        displayName: changingToGym.displayName,
        gymName: changingToGym.name,
      },
    };
  };

  public moveUserStripeAccountToNewPlanId = async (
    userId: string,
    planId: string,
    gymEnvironment: GymEnvironment,
    gymName: string,
    subscribeToPlanId?: string,
    coupon?: string,
  ): Promise<void> => {
    const user = await this.userRepo.getUserProfileOrThrow(userId);
    const { currentPlanId } = user;

    if (BaseUserProfile.hasActivePlan(user)) {
      const { currentPlan } = await this.paymentService.getPaymentInfo(
        user,
        user.currentPlanId,
      );
      if (currentPlan && currentPlan.subscription) {
        await this.paymentService.cancelSubscriptionImmediately(user, currentPlan.subscription.id, currentPlan.id);
        await this.updateUserSubscriptionDetails(userId);
      } else {
        throw new Error(`Failed to load existing subscription for uid ${user.uid}`);
      }
    }

    const newAccountId = this.paymentService.getAccountIdForPlanId(planId);
    const newGym = DbGymRepository.getByName(gymName);
    if (!newGym || !newGym.dummyPriceIdForSignup) {
      throw new Error('Invalid accountId, expected gym to have dummyPriceIdForSignup');
    }

    await this.paymentService.updateStripeCustomer(
      user.stripeUserId,
      { name: `Migrated to ${newGym.name}` },
      currentPlanId,
    );

    const newStripeCustomer = await this.paymentService.createStripeCustomer(user.uid, user.email, newAccountId);

    initializeStripeDetails(
      user,
      newStripeCustomer as IStripeCustomerDetailsWrapper,
      newGym.dummyPriceIdForSignup,
      this.paymentService,
    );
    user.currentPlanStatus = 'canceled';
    user.gymName = newGym.name;

    await this.updateUserProfile(user.uid, user);

    if (subscribeToPlanId) {
      await this.paymentService.subscribeToPlan({
        userProfile: user,
        planId: subscribeToPlanId,
        couponCode: coupon,
        onceOffPriceIds: [],
      });
    }
  };

  public async subscribeNewUserToApplicableFreePlanIfAllowed(
    uid: string,
    gym: Gym,
    accountStatusManager: AccountStatusManager,
  ): Promise<boolean> {
    const userProfile = await this.userRepo.getUserProfileOrThrow(uid);

    if (!this.isUserEligibleForInitialFreePlan(userProfile, gym)) {
      return false;
    }

    const createSubscriptionArguments = this.getNewUserSubscriptionDetailsIfAny(userProfile, gym);
    if (createSubscriptionArguments == null) {
      return false;
    }

    const accountStatus = await accountStatusManager.changeAccountStatusUsingPlanId(
      userProfile,
      gym.environment,
      undefined,
      createSubscriptionArguments,
    );
    return accountStatus.success;
  }

  public async subscribeOrUpdateUserSubscriptionToPlan(
    accountStatusManager: AccountStatusManager,
    gymEnvironment: GymEnvironment,
    specialOfferManager: SpecialOfferManager,
    userId: string,
    planId: string,
    coupon?: string,
    pendingPlanId?: string,
    pendingCoupon?: string,
    stripeCustomerIdCreatedBy3rdParty?: string,
    specialOfferId?: string,
  ): Promise<IAccountChangeStatus> {
    const user = await this.userRepo.getUserProfileOrThrow(userId);

    let billingStartDate;
    if (specialOfferId) {
      const specialOffer = specialOfferManager.getSpecialFromUser(user, specialOfferId);
      billingStartDate = new Date(specialOffer.billingStartDate);
    }

    await this.setStripeIdIfExistingCustomerDoesNotHavePaymentSource(
      user,
      planId,
      stripeCustomerIdCreatedBy3rdParty,
    );
    const createSubscriptionArguments: ISubscriptionCreateArguments = {
      userProfile: user,
      planId,
      onceOffPriceIds: [],
      couponCode: coupon,
      pendingPlanId,
      pendingCoupon,
      billingStartDate,
    };

    const changeResult = await accountStatusManager.changeAccountStatusUsingPlanId(
      user,
      gymEnvironment,
      undefined,
      createSubscriptionArguments,
    );
    const subscription = changeResult.data;
    await this.updateUserSubscriptionDetails(
      userId,
      subscription.id,
      subscription.plan?.id,
    );
    return subscription;
  }

  public updateUserDefaultEquipment(uid: string, equipment: Equipment[]) {
    return this.updateSpecificFieldsWithConflictRetry(uid, { defaultEquipment: equipment });
  }

  public async updateUserDefaultEquipmentToProductEquipment(
    userProfile: BaseUserProfile,
    plan: Stripe.Plan,
  ) {
    const productMetaData = await this.paymentService.productMetadata(plan);
    if (BaseEquipment.isHomeBenchBandsEquipmentSelection(productMetaData.equipment)) {
      await this.updateUserDefaultEquipment(
        userProfile.uid,
        BaseEquipment.EquipmentCombinationList[EquipmentCombinations.BENCH_BANDS] as Equipment[],
      );
    } else if (BaseEquipment.isHomeDumbbellsEquipmentSelection(productMetaData.equipment)) {
      await this.updateUserDefaultEquipment(
        userProfile.uid,
        BaseEquipment.EquipmentCombinationList[EquipmentCombinations.DUMBBELLS] as Equipment[],
      );
    } else if (BaseEquipment.isBenchDumbbellsEquipmentSelection(productMetaData.equipment)) {
      await this.updateUserDefaultEquipment(
        userProfile.uid,
        BaseEquipment.EquipmentCombinationList[EquipmentCombinations.BENCH_HUB_DUMBBELLS] as Equipment[],
      );
    }
  }

  public async updateUserHeartRateMonitors(userId: string, heartRateMonitorsUpdate: IHeartRateMonitors) {
    const userProfile = await this.userRepo.getUserProfileOrThrow(userId);
    userProfile.heartRateMonitors = {
      ...userProfile.heartRateMonitors,
      ...heartRateMonitorsUpdate,
    };
    await this.userRepo.update(userProfile);
    return userProfile;
  }

  public async updateUserWorkoutImportConfig(
    user: UserProfile,
    workoutImportConfig: IGarminWorkoutImportConfig
    | IAppleHealthWorkoutImportConfig
    | IAndroidHealthConnectWorkoutImportConfig,
  ) {
    if (!user?.WorkoutImportConfig) {
      user.WorkoutImportConfig = [];
      user.WorkoutImportConfig.push(workoutImportConfig);
    } else {
      const userHasOriginWorkoutImportConfig = user.WorkoutImportConfig.some((WIC) =>
        WIC.origin = workoutImportConfig.origin);
      if (userHasOriginWorkoutImportConfig) {
        const updatedWorkoutImportConfig = user.WorkoutImportConfig.map((WIC) =>
          (WIC.origin === workoutImportConfig.origin
            ? { ...WIC, ...workoutImportConfig }
            : WIC));
        user.WorkoutImportConfig = updatedWorkoutImportConfig;
      } else {
        user.WorkoutImportConfig.push(workoutImportConfig);
      }
    }
    await this.updateUserProfile(user.uid, user);
  }

  private getNewUserSubscriptionDetailsIfAny(
    userProfile: BaseUserProfile,
    gym: Gym,
  ): ISubscriptionCreateArguments | undefined {
    let planId = gym.subscribeNewUsersToPlanAfterFirstOnboarding?.planId;
    let couponCode = gym.subscribeNewUsersToPlanAfterFirstOnboarding?.coupon;

    if (userProfile.companyId != null) {
      const company = gym.companies?.find((c) => c.id === userProfile.companyId);
      if (company && company.newUserDefaultStripePlan) {
        planId = company.newUserDefaultStripePlan;
        couponCode = company.newUserDefaultStripeCoupon;
        if (couponCode == null || couponCode === '') { // run time check
          this.logger.warn(`Company new user coupon required for company ${company.id} in gym ${gym.name}`);
        }
      }
    }

    if (planId == null || planId === '') {
      return undefined;
    }

    return {
      userProfile,
      planId,
      onceOffPriceIds: [],
      couponCode,
    };
  }

  private isUserEligibleForInitialFreePlan(userProfile: UserProfile, gym: Gym): boolean {
    const isNew = userProfile.currentPlanId === gym.dummyPriceIdForSignup
      && userProfile.planHistory?.length === 0;
    return isNew;
  }

  private getUserProfileAttributesFromProductMetadata(plan: Stripe.Plan) {
    return this.paymentService.productMetadata(plan);
  }

  private signupPreAccount = async (
    userParams: Partial<UserProfile>,
    inputValidationFunction: (fields: any) => void,
  ): Promise<UserProfile> => {
    if (!userParams.email) {
      throw new Error('Unable to signup. Email required to create a pre-account');
    }
    if (!userParams.gymName) {
      throw new Error('Gym name is required for a pre-account signup');
    }
    const gym = DbGymRepository.getByName(userParams.gymName);
    if (!gym.dummyPriceIdForSignup) {
      throw new Error(`${gym.name} not configured with a dummyPriceIdForSignup`);
    }

    const existing = await this.userRepo.getUserByEmail(userParams.email);
    if (existing) {
      throw new Error(`Unable to signup. PreAccount already exists for email ${userParams.email}`);
    }

    const userProfile = initializeUserFromSubsetOfFields(
      userParams,
      null, // assume new user on firebase
      inputValidationFunction,
    );

    const findExistingUserOnFirebase = this.firebaseAdmin.getUserIdByEmail(userParams.email);
    await Promise.all([
      createContinueSignupLink(userProfile, this.dynamicLinkService),
      this.createStripeUser(userProfile, gym.accountId, gym.dummyPriceIdForSignup),
    ]);

    const firebaseUserId = await findExistingUserOnFirebase;
    if (firebaseUserId) {
      await this.linkNewUserToExistingFirebaseUser(userProfile, firebaseUserId, gym.accountId);
    }

    await this.userRepo.update(userProfile);
    this.marketingAnalyticsService.logEventInBackground('sign_up_preaccount_created', userProfile);
    return userProfile;
  };

  private async createStripeUser(userProfile: UserProfile, accountId: string, dummyPriceIdForSignup: string) {
    const stripeUser = await this.paymentService.createStripeCustomer(
      userProfile.uid,
      userProfile.email,
      accountId,
    ) as IStripeCustomerDetailsWrapper;

    initializeStripeDetails(
      userProfile,
      stripeUser,
      dummyPriceIdForSignup,
      this.paymentService,
    );
  }

  private async linkNewUserToExistingFirebaseUser(
    userProfile: UserProfile,
    firebaseUserId: string,
    accountId: string,
  ) {
    this.logger.warn(`Using existed Firebase user ${firebaseUserId} for 'new user' ${userProfile.email}`);
    userProfile.uid = firebaseUserId;
    (userProfile as any)._id = userProfile.uid; // TODO: move _id setup into the repository

    await Promise.all([
      createContinueSignupLink(userProfile, this.dynamicLinkService),
      this.paymentService.setUidForStripeCustomer(userProfile.stripeUserId, userProfile.uid, accountId),
    ]);
  }

  private errorIsNotFound = (e: any): boolean => e.status === 404;

  private updateSpecificFieldsWithConflictRetry = async (uid: string, update: Partial<UserProfile>) => {
    let currentTryCount = 0;
    const tryUpdate = async (): Promise<UserProfile> => {
      const currentProfile: any = await this.userSettingsDb.get(uid);
      const updatedProfile = {
        ...currentProfile,
        ...update,
      };
      currentTryCount++;
      try {
        this.logger.info(`updating user ${uid}, retry count ${currentTryCount}`);
        const updateResult = await this.updateUserProfile(uid, updatedProfile);
        return updateResult;
      } catch (e) {
        const isConflict = e.status === 409;
        if (isConflict && currentTryCount < 3) {
          this.logger.warn(`conflict while updating user ${uid}, trying again`);
          return await tryUpdate();
        }
        this.logger.error(`failed to update ${uid}`, e);
        throw e;
      }
    };
    return tryUpdate();
  };

  private hasStripeBillingChanged = (userProfile: any, updatedUserProfile: any): boolean => {
    const userProfileString = this.stringifyBilling(userProfile);
    const updatedUserProfileString = this.stringifyBilling(updatedUserProfile);
    return userProfileString !== updatedUserProfileString;
  };

  private hasSubscriptionUpdateDetailsChanged = (userProfile: any, updatedUserProfile: any): boolean => {
    const userProfileString = this.stringifySubscriptionUpdate(userProfile);
    const updatedUserProfileString = this.stringifySubscriptionUpdate(updatedUserProfile);
    return userProfileString !== updatedUserProfileString;
  };

  private hasSubscriptionRemoveDetailsChanged = (userProfile: any, updatedUserProfile: any): boolean => {
    const userProfileString = this.stringifySubscriptionRemove(userProfile);
    const updatedUserProfileString = this.stringifySubscriptionRemove(updatedUserProfile);
    return userProfileString !== updatedUserProfileString;
  };

  private stringifyBilling = (userProfile: UserProfile): string => {
    const billing = {
      addressLine1: userProfile.addressLine1,
      addressLine2: userProfile.addressLine2,
      state: userProfile.state,
      suburb: userProfile.suburb,
      postCode: userProfile.postCode,
      country: userProfile.country,
      fullname: userProfile.fullname,
      mobileNumber: userProfile.mobileNumber,
    };

    return JSON.stringify(billing);
  };

  private stringifySubscriptionUpdate = (userProfile: Partial<UserProfile>): string => {
    const subscription = {
      currentPlanId: userProfile.currentPlanId,
      currentPlanExpiresAt: userProfile.currentPlanExpiresAt,
      timeSlotType: userProfile.timeSlotType,
      currentPlanStatus: userProfile.currentPlanStatus,
      currentPlanCancelAtEndOfTerm: userProfile.currentPlanCancelAtEndOfTerm,
      isFamilyPlanAdmin: userProfile.isFamilyPlanAdmin,
      stripeSubscriptionData: userProfile.stripeSubscriptionData,
    };

    return JSON.stringify(subscription);
  };

  private stringifySubscriptionRemove = (userProfile: any): string => {
    const subscription = {
      timeSlotType: userProfile.timeSlotType,
      currentPlanStatus: userProfile.currentPlanStatus,
    };

    return JSON.stringify(subscription);
  };
}
